<template>
  <div
    ref="rootEl"
    role="region"
    :aria-roledescription="t('sections.cases.carousel.roledescription')"
    :aria-label="t('sections.cases.carousel.label')"
    class="relative"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
    @focusin="focused = true"
    @focusout="onFocusOut"
  >
    <div class="overflow-hidden">
      <div
        ref="trackEl"
        class="flex"
        :class="{ 'transition-transform duration-500 ease-out': transitionEnabled }"
        :style="trackStyle"
        @transitionend="onTransitionEnd"
      >
        <div
          v-for="(slide, index) in slides"
          :key="slide.key"
          class="w-full shrink-0 px-3 md:w-1/2"
          role="group"
          aria-roledescription="slide"
          :aria-label="
            t('sections.cases.carousel.slideLabel', {
              current: slide.realIndex + 1,
              total: count,
            })
          "
          :aria-hidden="isVisible(index) ? 'false' : 'true'"
        >
          <CaseCard
            :client="slide.props.client"
            :eyebrow="slide.props.eyebrow"
            :title="slide.props.title"
            :description="slide.props.description"
            :image="slide.props.image"
            :image-alt="slide.props.imageAlt"
            :accent="slide.props.accent"
            :accent-ink="slide.props.accentInk"
          />
        </div>
      </div>
    </div>

    <button
      type="button"
      class="absolute top-1/2 left-2 z-10 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-paper text-ink-strong shadow-lg transition-colors hover:bg-paper-page focus-visible:shadow-[var(--shadow-ring-focus)] focus-visible:outline-none md:left-4"
      :aria-label="t('sections.cases.carousel.prev')"
      data-testid="case-carousel-prev"
      @click="prev"
    >
      <BaseIcon name="chevron-left" :size="20" />
    </button>
    <button
      type="button"
      class="absolute top-1/2 right-2 z-10 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-paper text-ink-strong shadow-lg transition-colors hover:bg-paper-page focus-visible:shadow-[var(--shadow-ring-focus)] focus-visible:outline-none md:right-4"
      :aria-label="t('sections.cases.carousel.next')"
      data-testid="case-carousel-next"
      @click="next"
    >
      <BaseIcon name="chevron-right" :size="20" />
    </button>

    <div
      class="mt-6 flex justify-center gap-2"
      role="tablist"
      :aria-label="t('sections.cases.carousel.label')"
    >
      <button
        v-for="i in count"
        :key="i"
        type="button"
        role="tab"
        class="h-2.5 w-2.5 rounded-full transition-colors focus-visible:shadow-[var(--shadow-ring-focus)] focus-visible:outline-none"
        :class="i - 1 === activeIndex ? 'bg-accent-strong' : 'bg-stroke hover:bg-ink-muted'"
        :aria-label="t('sections.cases.carousel.goTo', { n: i })"
        :aria-selected="i - 1 === activeIndex ? 'true' : 'false'"
        @click="goTo(i - 1)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * `CaseCarousel` — бесконечный слайдер кейсов с автолистанием.
 *
 * - Видимо одновременно: 1 карточка на mobile, 2 на md+ (`matchMedia
 *   '(min-width: 768px)'`).
 * - Бесконечность реализована через клоны: с обеих сторон ленты добавлено
 *   по `visibleCount` копий, после `transitionend` индекс «прыгает» на
 *   эквивалентный реальный без анимации (`transitionEnabled = false` →
 *   `requestAnimationFrame` → `true`).
 * - Автолистание раз в 10 секунд, пауза при `:hover`/`:focus-within`/
 *   скрытой вкладке/`prefers-reduced-motion: reduce`.
 * - Reduced-motion: transition выключается полностью, snap происходит
 *   синхронно (без `transitionend`-события), автолистание не стартует.
 *
 * Полный UI: стрелки prev/next по бокам и пагинация-доты под лентой.
 * Каждый слайд получает `role="group"`, `aria-roledescription="slide"`
 * и `aria-label` вида «N of M», корень — `role="region"` +
 * `aria-roledescription="carousel"`.
 *
 * Props принимает уже локализованный view-model — пусть `HomeCases.vue`
 * через `pickLocalized` готовит данные, а карусель остаётся
 * представлением.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import BaseIcon from '@/components/base/BaseIcon.vue';
import CaseCard from '@/components/domain/CaseCard.vue';
import { useReducedMotion } from '@/composables/useReducedMotion';

import type { CaseAccentInk } from '@/content/home-data';

export interface CaseSlideViewModel {
  readonly id: string;
  readonly client: string;
  readonly eyebrow: string;
  readonly title: string;
  readonly description: string;
  readonly image: string;
  readonly imageAlt: string;
  readonly accent: string;
  readonly accentInk: CaseAccentInk;
}

interface Props {
  cases: readonly CaseSlideViewModel[];
}

const props = defineProps<Props>();

const AUTOPLAY_INTERVAL_MS = 10_000;
const DESKTOP_BREAKPOINT = '(min-width: 768px)';

const { t } = useI18n();
const prefersReducedMotion = useReducedMotion();

const rootEl = ref<HTMLElement | null>(null);
const trackEl = ref<HTMLElement | null>(null);
const visibleCount = ref<number>(1);
const currentIndex = ref<number>(1);
const activeIndex = ref<number>(0);
const internalTransition = ref<boolean>(true);
const transitioning = ref<boolean>(false);
const hovered = ref<boolean>(false);
const focused = ref<boolean>(false);
const documentHidden = ref<boolean>(false);

let autoplayId: number | null = null;
let desktopMql: MediaQueryList | null = null;

interface Slide {
  readonly key: string;
  readonly realIndex: number;
  readonly props: CaseSlideViewModel;
}

const count = computed<number>(() => props.cases.length);

const slides = computed<readonly Slide[]>(() => {
  const v = visibleCount.value;
  const arr = props.cases;
  const n = arr.length;
  if (n === 0) return [];
  const result: Slide[] = [];
  for (let i = 0; i < v; i += 1) {
    const ri = (n - v + i + n) % n;
    const item = arr[ri];
    result.push({ key: `head-${i}-${item.id}`, realIndex: ri, props: item });
  }
  for (let i = 0; i < n; i += 1) {
    const item = arr[i];
    result.push({ key: `real-${i}-${item.id}`, realIndex: i, props: item });
  }
  for (let i = 0; i < v; i += 1) {
    const item = arr[i];
    result.push({ key: `tail-${i}-${item.id}`, realIndex: i, props: item });
  }
  return result;
});

const trackStyle = computed<Record<string, string>>(() => {
  const slideWidth = 100 / visibleCount.value;
  return { transform: `translateX(-${currentIndex.value * slideWidth}%)` };
});

const transitionEnabled = computed<boolean>(
  () => internalTransition.value && !prefersReducedMotion.value,
);

const paused = computed<boolean>(
  () => hovered.value || focused.value || documentHidden.value || prefersReducedMotion.value,
);

function isVisible(index: number): boolean {
  return index >= currentIndex.value && index < currentIndex.value + visibleCount.value;
}

function snapIfNeeded(): void {
  const v = visibleCount.value;
  const n = count.value;
  const ci = currentIndex.value;
  if (ci < v) {
    currentIndex.value = ci + n;
  } else if (ci >= n + v) {
    currentIndex.value = ci - n;
  }
}

function commitNav(targetCurrent: number, newReal: number): void {
  if (transitioning.value) return;
  activeIndex.value = newReal;
  internalTransition.value = true;
  currentIndex.value = targetCurrent;
  if (prefersReducedMotion.value) {
    snapIfNeeded();
    transitioning.value = false;
    return;
  }
  transitioning.value = true;
}

function next(): void {
  const newReal = (activeIndex.value + 1) % count.value;
  commitNav(currentIndex.value + 1, newReal);
}

function prev(): void {
  const newReal = (activeIndex.value - 1 + count.value) % count.value;
  commitNav(currentIndex.value - 1, newReal);
}

function goTo(realIndex: number): void {
  if (realIndex === activeIndex.value) return;
  commitNav(realIndex + visibleCount.value, realIndex);
}

async function onTransitionEnd(event: TransitionEvent): Promise<void> {
  if (event.target !== trackEl.value || event.propertyName !== 'transform') return;
  const v = visibleCount.value;
  const n = count.value;
  const ci = currentIndex.value;
  if (ci < v || ci >= n + v) {
    internalTransition.value = false;
    currentIndex.value = ci < v ? ci + n : ci - n;
    await nextTick();
    requestAnimationFrame(() => {
      internalTransition.value = true;
    });
  }
  transitioning.value = false;
}

function onFocusOut(event: FocusEvent): void {
  const nextTarget = event.relatedTarget as Node | null;
  if (nextTarget && rootEl.value && rootEl.value.contains(nextTarget)) return;
  focused.value = false;
}

function onVisibleCountChange(event: MediaQueryListEvent): void {
  visibleCount.value = event.matches ? 2 : 1;
}

function onVisibilityChange(): void {
  documentHidden.value = document.visibilityState === 'hidden';
}

function startAutoplay(): void {
  if (autoplayId !== null) return;
  if (typeof window === 'undefined') return;
  autoplayId = window.setInterval(() => {
    if (paused.value || transitioning.value) return;
    next();
  }, AUTOPLAY_INTERVAL_MS);
}

function stopAutoplay(): void {
  if (autoplayId === null) return;
  clearInterval(autoplayId);
  autoplayId = null;
}

watch(visibleCount, (newV) => {
  internalTransition.value = false;
  currentIndex.value = activeIndex.value + newV;
  void nextTick().then(() => {
    requestAnimationFrame(() => {
      internalTransition.value = true;
    });
  });
});

onMounted(() => {
  if (typeof window === 'undefined') return;

  if (typeof window.matchMedia === 'function') {
    desktopMql = window.matchMedia(DESKTOP_BREAKPOINT);
    visibleCount.value = desktopMql.matches ? 2 : 1;
    if (typeof desktopMql.addEventListener === 'function') {
      desktopMql.addEventListener('change', onVisibleCountChange);
    } else {
      const legacy = desktopMql as MediaQueryList & {
        addListener: (l: (e: MediaQueryListEvent) => void) => void;
      };
      legacy.addListener(onVisibleCountChange);
    }
  }

  currentIndex.value = visibleCount.value;
  activeIndex.value = 0;

  if (typeof document !== 'undefined') {
    documentHidden.value = document.visibilityState === 'hidden';
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  startAutoplay();
});

onBeforeUnmount(() => {
  stopAutoplay();
  if (desktopMql) {
    if (typeof desktopMql.removeEventListener === 'function') {
      desktopMql.removeEventListener('change', onVisibleCountChange);
    } else {
      const legacy = desktopMql as MediaQueryList & {
        removeListener: (l: (e: MediaQueryListEvent) => void) => void;
      };
      legacy.removeListener(onVisibleCountChange);
    }
    desktopMql = null;
  }
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }
});
</script>
